import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  TrendingUp,
  DollarSign,
  Percent
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SalaryUpgradePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [cargos, setCargos] = useState([]);
  const [upgrades, setUpgrades] = useState({});

  useEffect(() => {
    fetchCargos();
  }, []);

  const fetchCargos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/job-positions?active_only=true`);
      const jobsWithRanges = await Promise.all(response.data.map(async (job) => {
        const ranges = await axios.get(`${API}/job-positions/${job.id}/salary-ranges`);
        return ranges.data.map((range) => ({ ...range, cargo: job.name, empresa: job.company_name }));
      }));
      const salaryRanges = jobsWithRanges.flat();
      setCargos(salaryRanges);

      const initialUpgrades = {};
      salaryRanges.forEach(range => {
        initialUpgrades[range.id] = 0;
      });
      setUpgrades(initialUpgrades);
    } catch (error) {
      console.error('Erro ao carregar cargos:', error);
      toast.error('Erro ao carregar cargos');
    } finally {
      setLoading(false);
    }
  };

  const handlePercentageChange = (rangeId, value) => {
    const sanitized = value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
    const val = parseFloat(sanitized) || 0;
    setUpgrades(prev => ({
      ...prev,
      [rangeId]: Math.min(Math.max(val, 0), 100)
    }));
  };

  const handleUpgrade = async () => {
    try {
      setUpgrading(true);
      
      // Prepare upgrades array
      const upgradeArray = Object.entries(upgrades)
        .filter(([, percentage]) => percentage > 0)
        .map(([range_id, percentage]) => ({
          range_id,
          percentage
        }));

      if (upgradeArray.length === 0) {
        toast.error('Defina pelo menos uma porcentagem de aumento');
        return;
      }

      await axios.post(`${API}/job-positions/salary-ranges/upgrade`, {
        upgrades: upgradeArray
      });

      toast.success('Salários atualizados com sucesso!');
      
      // Reset percentages
      const resetUpgrades = {};
      cargos.forEach(range => {
        resetUpgrades[range.id] = 0;
      });
      setUpgrades(resetUpgrades);
      
    } catch (error) {
      console.error('Erro ao atualizar salários:', error);
      toast.error('Erro ao atualizar salários');
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/configuracoes/cargos-salarios')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Voltar</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Upgrade de Salários</h1>
            <p className="text-slate-600 mt-1">
              Atualize os salários em massa por porcentagem
            </p>
          </div>
        </div>
        <Button
          onClick={handleUpgrade}
          disabled={upgrading}
          className="btn-success flex items-center space-x-2"
        >
          {upgrading ? (
            <div className="loading-spinner w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <TrendingUp className="h-4 w-4" />
          )}
          <span>Upgrade</span>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <DollarSign className="h-5 w-5" />
            <span>Atualização de Salários por Cargo</span>
          </CardTitle>
          <CardDescription>
            Defina a porcentagem de aumento por cargo e modalidade.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cargos.map((range, index) => (
              <div key={range.id} className="space-y-2">
                <Label htmlFor={`cargo-${index}`} className="font-medium">
                  {range.cargo} <span className="text-sm font-normal text-slate-500">· {range.empresa} · {range.modalidade}</span>
                </Label>
                <div className="relative">
                  <Input
                    id={`cargo-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={upgrades[range.id] || ''}
                    onChange={(e) => handlePercentageChange(range.id, e.target.value)}
                    onKeyDown={(e) => { if (['-', '+', 'e', 'E'].includes(e.key)) e.preventDefault(); }}
                    placeholder="0.00"
                    className="pr-8"
                  />
                  <Percent className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                </div>
              </div>
            ))}
          </div>
          
          {cargos.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <DollarSign className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum cargo cadastrado</p>
              <p className="text-sm">Cadastre cargos primeiro para poder fazer upgrade</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle>Como funciona o Upgrade</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              <strong>1.</strong> Defina a porcentagem de aumento para cada cargo que deseja atualizar
            </p>
            <p>
              <strong>2.</strong> O sistema aplicará o aumento em todas as faixas salariais do cargo (Júnior I, II, III, Pleno I, II, III, Sênior I, II, III)
            </p>
            <p>
              <strong>3.</strong> Exemplo: Se um cargo tem salário Júnior I de R$ 3.000,00 e você aplicar 10% de aumento, o novo valor será R$ 3.300,00
            </p>
            <p>
              <strong>4.</strong> Deixe em 0 (zero) os cargos que não devem receber aumento
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SalaryUpgradePage;
